import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectAuth } from '../state/auth/auth.selector';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  MainPermission,
  Permission,
  Rol,
} from '../interfaces/permision.interface';

type GuardProfile = {
  idusuario: number;
  rol?: Rol | null;
};

@Injectable({
  providedIn: 'root',
})
export class RoleGuard {
  public profile$ = this.store.select(selectAuth);
  public profile: GuardProfile | null = null;
  private profileSubject = new BehaviorSubject<GuardProfile | false>(false);
  profileSubscription!: Subscription;
  profileSubject$: Observable<GuardProfile | false> =
    this.profileSubject.asObservable();

  constructor(private _router: Router, private store: Store) {
    this.profileInitializer();
  }

  profileInitializer() {
    this.profile$.subscribe((profile) => {
      if (profile.idusuario !== -1) {
        this.profileSubject.next(profile);
      }
    });
  }

  canActivate(route: ActivatedRouteSnapshot): any {
    const requiredPermission = route.data.permission;
    const requiredPermissions = Array.isArray(requiredPermission)
      ? requiredPermission
      : [requiredPermission];

    const value = (this.profileSubscription = this.profileSubject$.subscribe(
      (profile) => {
        if (!profile) {
          return false;
        }

        this.profile = profile;
        if (profile.idusuario !== -1) {
          if (this.profile.rol) {
            const permisosPrincipalesSet = new Set(
              this.profile.rol.permisos_principales.map(
                (permiso_principal: MainPermission) => permiso_principal.id,
              ),
            );

            const subPermissionsSet = new Set(
              this.profile.rol.permisos_principales.flatMap(
                (permiso_principal: MainPermission) =>
                  permiso_principal.permisos.map(
                    (permiso: Permission) => permiso.id,
                  ),
              ),
            );

            const mainPermissionNamesSet = new Set(
              this.profile.rol.permisos_principales
                .map(
                  (permiso_principal: MainPermission) =>
                    permiso_principal.nombre_permiso,
                )
                .filter(Boolean),
            );

            const subPermissionNamesSet = new Set(
              this.profile.rol.permisos_principales.flatMap(
                (permiso_principal: MainPermission) =>
                  permiso_principal.permisos
                    .map((permiso: Permission) => permiso.nombre_permiso)
                    .filter(Boolean),
              ),
            );

            const hasRequiredPermission =
              !requiredPermission ||
              requiredPermissions.some((permission: number | string | null | undefined) => {
                if (permission === null || permission === undefined) {
                  return false;
                }

                if (typeof permission === 'number') {
                  return (
                    permisosPrincipalesSet.has(permission) ||
                    subPermissionsSet.has(permission)
                  );
                }

                return (
                  mainPermissionNamesSet.has(permission) ||
                  subPermissionNamesSet.has(permission)
                );
              });

            if (hasRequiredPermission) {
              if (this.profileSubscription) {
                this.profileSubscription.unsubscribe();
              }
              return true;
            } else {
              this._router.navigateByUrl('/');
              if (this.profileSubscription) {
                this.profileSubscription.unsubscribe();
              }
              return false;
            }
          } else {
            if (this.profileSubscription) {
              this.profileSubscription.unsubscribe();
            }
            return true;
          }
        } else {
          this._router.navigateByUrl('/');
          if (this.profileSubscription) {
            this.profileSubscription.unsubscribe();
          }
          return false;
        }
      },
    ));
    return value;
  }
}
